import { mutate } from 'swr';

import { DiagramSnippetHelpers } from '@diagram/helpers/DiagramSnippetHelpers';
import { SidebarService } from '@diagram/services/SidebarService';
import { SidebarType } from '@diagram/state/sidebar';

import { LocalNotificationService } from 'services/LocalNotificationService';
import { ModalService } from 'services/ModalService';
import { PayloadSDK } from 'services/PayloadSDK';

import type { DiagramSnippet } from '@/payload-types';

const DiagramSnippetService = {
    list: async () => {
        return await PayloadSDK.find({
            collection: 'diagramSnippets',
            depth: 0,
            pagination: false,
        });
    },

    create: async (data: Pick<DiagramSnippet, 'name'> & Partial<DiagramSnippet>) => {
        return await PayloadSDK.create({
            collection: 'diagramSnippets',
            data: data as DiagramSnippet,
        });
    },

    update: async (id: string, data: Partial<DiagramSnippet>) => {
        return await PayloadSDK.update({
            collection: 'diagramSnippets',
            id,
            data,
        });
    },

    delete: async (id: string) => {
        return await PayloadSDK.delete({
            collection: 'diagramSnippets',
            id,
        });
    },

    createFromSelection: async (componentInstanceIds: string[]) => {
        ModalService.open({
            modalId: 'saving-snippet',
            title: 'Save as snippet',
            withCloseButton: false,
            closeOnClickOutside: false,
            closeOnEscape: false,
            children: 'Saving...',
        });

        try {
            const { name, componentInstances, connections } =
                DiagramSnippetHelpers.prepareSnippetData(componentInstanceIds);

            await DiagramSnippetService.create({
                name,
                componentInstances,
                connections,
            });

            await mutate(DiagramSnippetHelpers.swr.list, undefined, { revalidate: true });
            SidebarService.open(SidebarType.SNIPPETS);
            LocalNotificationService.showSuccess({ message: 'Snippet saved' });
        } catch {
            LocalNotificationService.showError({ message: 'Failed to save snippet' });
        } finally {
            ModalService.closeAll();
        }
    },
};

export { DiagramSnippetService };
